// Navegación y acciones por rol (spec funcional §4 y §9.4; matriz spec técnica §6.5).
// La autorización real la hace el API (guard + RLS); aquí solo se decide qué mostrar.
import type { Usuario } from '../api/types';

export type Perfil = 'administrador' | 'remuneraciones' | 'adm_campo' | 'capataz' | 'planillero' | 'inspector' | 'contratista' | 'auditor';

export type Capacidad =
  | 'inicio'
  | 'onboarding'
  | 'maestros.ver'
  | 'maestros.editar'
  | 'mi_gente'
  | 'carga_masiva'
  | 'tarifas.ver'
  | 'tarifas.mantener'
  | 'tarifas.aprobar'
  | 'jornadas.validar'
  | 'eventos.ver'
  | 'eventos.crear'
  | 'tarja.registrar'
  | 'eventos.anular'
  | 'cuarentena.ver'
  | 'cuarentena.resolver'
  | 'repartos.ver'
  | 'repartos.aprobar'
  | 'reclamos.ver'
  | 'reclamos.crear'
  | 'reclamos.resolver'
  | 'periodo.ver'
  | 'periodo.operar'
  | 'periodo.aprobar_campo'
  | 'periodo.aprobar_contratista'
  | 'periodo.cierre'
  | 'nomina'
  | 'reportes'
  | 'reportes.contratista'
  | 'auditoria'
  | 'integraciones'
  | 'dispositivos'
  | 'configuracion'
  | 'usuarios';

export const ETIQUETA_PERFIL: Record<Perfil, string> = {
  administrador: 'Administrador',
  remuneraciones: 'Remuneraciones',
  adm_campo: 'Administrador de campo',
  capataz: 'Capataz',
  planillero: 'Planillero',
  inspector: 'Inspector de calidad',
  contratista: 'Contratista',
  auditor: 'Auditoría',
};

export function perfiles(u: Usuario): Perfil[] {
  const permisos = new Set(u.permisos.map((p) => p.permiso));
  const r: Perfil[] = [];
  if (u.rol_base === 'administrador') r.push('administrador');
  if (u.tipo_usuario === 'externo' || permisos.has('aprobador_contratista')) r.push('contratista');
  if (permisos.has('remuneraciones') || permisos.has('cierre_periodo')) r.push('remuneraciones');
  if (permisos.has('mantenedor_tarifas') || permisos.has('aprobador_tarifas')) r.push('adm_campo');
  if (permisos.has('capataz')) r.push('capataz');
  if (permisos.has('planillero')) r.push('planillero');
  if (permisos.has('inspector_calidad')) r.push('inspector');
  if (permisos.has('auditor')) r.push('auditor');
  return r;
}

const POR_PERFIL: Record<Perfil, Capacidad[]> = {
  administrador: [
    'inicio', 'onboarding', 'maestros.ver', 'maestros.editar', 'carga_masiva', 'tarifas.ver', 'eventos.ver', 'cuarentena.ver',
    'cuarentena.resolver', 'repartos.ver', 'reclamos.ver', 'periodo.ver', 'nomina', 'reportes', 'auditoria', 'integraciones',
    'dispositivos', 'configuracion', 'usuarios', 'tarja.registrar',
  ],
  remuneraciones: [
    'inicio', 'maestros.ver', 'tarifas.ver', 'eventos.ver', 'cuarentena.ver', 'cuarentena.resolver', 'reclamos.ver', 'reclamos.resolver',
    'periodo.ver', 'periodo.operar', 'periodo.aprobar_campo', 'periodo.cierre', 'nomina', 'reportes',
  ],
  adm_campo: [
    'inicio', 'maestros.ver', 'maestros.editar', 'tarifas.ver', 'eventos.ver', 'cuarentena.ver', 'cuarentena.resolver', 'repartos.ver',
    'repartos.aprobar', 'reclamos.ver', 'reclamos.resolver', 'periodo.ver', 'periodo.operar', 'periodo.aprobar_campo', 'reportes',
  ],
  capataz: ['inicio', 'maestros.ver', 'tarifas.ver', 'jornadas.validar', 'eventos.ver', 'eventos.crear', 'eventos.anular', 'repartos.ver', 'cuarentena.ver', 'reclamos.ver', 'reclamos.crear', 'reportes', 'tarja.registrar'],
  planillero: ['inicio', 'eventos.ver', 'eventos.crear', 'tarja.registrar'],
  inspector: ['inicio', 'eventos.ver', 'eventos.crear'],
  // El contratista ve solo su gente, sus aprobaciones y su reporte de producción e importes. Nunca tarifas.
  contratista: ['inicio', 'mi_gente', 'eventos.ver', 'repartos.ver', 'cuarentena.ver', 'periodo.ver', 'periodo.aprobar_contratista', 'reclamos.ver', 'reportes.contratista'],
  auditor: ['inicio', 'auditoria', 'reportes', 'periodo.ver', 'eventos.ver'],
};

export function capacidades(u: Usuario | null | undefined): Set<Capacidad> {
  const caps = new Set<Capacidad>();
  if (!u) return caps;
  const ps = perfiles(u);
  for (const p of ps) for (const c of POR_PERFIL[p]) caps.add(c);
  const permisos = new Set(u.permisos.map((p) => p.permiso));
  if (permisos.has('mantenedor_tarifas')) caps.add('tarifas.mantener');
  if (permisos.has('aprobador_tarifas')) caps.add('tarifas.aprobar');
  if (permisos.has('carga_masiva')) caps.add('carga_masiva');
  if (permisos.has('enrolador_dispositivos')) caps.add('dispositivos');
  // Contratista: nunca tarifas (aunque tuviera otro permiso asignado por error en su sociedad).
  if (ps.includes('contratista') && u.rol_base !== 'administrador') {
    caps.delete('tarifas.ver');
    caps.delete('tarifas.mantener');
    caps.delete('tarifas.aprobar');
    caps.delete('maestros.ver');
    caps.delete('maestros.editar');
  }
  if (caps.size === 0) caps.add('inicio');
  return caps;
}

export interface ItemNav {
  ruta: string;
  etiqueta: string;
  requiere: Capacidad[];
  grupo: 'Operación' | 'Cierre' | 'Maestros' | 'Administración';
}

export const NAVEGACION: ItemNav[] = [
  { ruta: '/', etiqueta: 'Inicio', requiere: ['inicio'], grupo: 'Operación' },
  { ruta: '/onboarding', etiqueta: 'Puesta en marcha', requiere: ['onboarding'], grupo: 'Operación' },
  { ruta: '/registrar-tarja', etiqueta: 'Registrar tarja', requiere: ['tarja.registrar'], grupo: 'Operación' },
  { ruta: '/jornadas', etiqueta: 'Jornadas por validar', requiere: ['jornadas.validar'], grupo: 'Operación' },
  { ruta: '/eventos', etiqueta: 'Eventos de tarja', requiere: ['eventos.ver'], grupo: 'Operación' },
  { ruta: '/cuarentena', etiqueta: 'Cuarentena', requiere: ['cuarentena.ver'], grupo: 'Operación' },
  { ruta: '/repartos', etiqueta: 'Reparto de cuadrilla', requiere: ['repartos.ver'], grupo: 'Operación' },
  { ruta: '/reclamos', etiqueta: 'Reclamos', requiere: ['reclamos.ver'], grupo: 'Operación' },
  { ruta: '/periodos', etiqueta: 'Periodos y cierre', requiere: ['periodo.ver'], grupo: 'Cierre' },
  { ruta: '/nomina', etiqueta: 'Lotes de nómina', requiere: ['nomina'], grupo: 'Cierre' },
  { ruta: '/reportes', etiqueta: 'Reportes', requiere: ['reportes', 'reportes.contratista'], grupo: 'Cierre' },
  { ruta: '/mi-gente', etiqueta: 'Mi gente', requiere: ['mi_gente'], grupo: 'Maestros' },
  { ruta: '/maestros', etiqueta: 'Maestros', requiere: ['maestros.ver'], grupo: 'Maestros' },
  { ruta: '/tarifarios', etiqueta: 'Tarifarios', requiere: ['tarifas.ver'], grupo: 'Maestros' },
  { ruta: '/auditoria', etiqueta: 'Auditoría', requiere: ['auditoria'], grupo: 'Administración' },
  { ruta: '/integraciones', etiqueta: 'Integraciones', requiere: ['integraciones'], grupo: 'Administración' },
  { ruta: '/dispositivos', etiqueta: 'Dispositivos', requiere: ['dispositivos'], grupo: 'Administración' },
  { ruta: '/equipo', etiqueta: 'Equipo', requiere: ['usuarios'], grupo: 'Administración' },
  { ruta: '/configuracion', etiqueta: 'Configuración', requiere: ['configuracion'], grupo: 'Administración' },
];

export function navegacionPara(caps: Set<Capacidad>): ItemNav[] {
  return NAVEGACION.filter((i) => i.requiere.some((c) => caps.has(c)));
}
